// src/features/analytics/useAnalyticsHeatmap.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS } from '@/lib/constants/analytics';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, HeatmapData } from './types';

export interface UseAnalyticsHeatmapResult {
  heatmap: AnalyticsEnvelope<HeatmapData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Concurrency heatmap — account scope only; the caller clamps the range to
 * ≤90 days (gateway presence-retention cap). Pass null cid to disable.
 */
export function useAnalyticsHeatmap(
  cid: number | null,
  range: UtcRange
): UseAnalyticsHeatmapResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.heatmap(cid ?? 0, range.from, range.to),
    queryFn: () => AnalyticsApi.getHeatmap(cid!, range),
    enabled: cid !== null,
    staleTime: 5 * 60 * 1000,
  });

  return {
    heatmap: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
